'use client'

import { UserCircleIcon } from '@phosphor-icons/react/dist/ssr'
import Image from 'next/image'

import { clearCartStorage } from '@/context/customer/customer.utils'
import { useCustomer } from '@/context/customer/CustomerContext'
import { handleLogout } from '@/lib/auth'
import { handleLogin } from '@/lib/auth/login'
import { asHttpSrc } from '@/lib/utils'

import { ContactUsBanner } from './ContactUsBanner'
import CopyrightFooter from './CopyrightFooter'
import { FooterLinks } from './FooterLinks'
import { Newsletter } from './Newsletter'
import { SocialIcons } from './SocialIcons'

import type { FooterType } from '@/types/contentful/footerType'

export const FooterClient = ({ data }: { data: FooterType['fields'] }) => {
  const { menus, logo, banner, socialIcons, newsletterLabel } = data
  const logoUrl = asHttpSrc(logo?.fields?.file?.url)
  const { customer } = useCustomer()
  const accountButtonLabel = !!customer ? 'Sign Out' : 'Sign In'
  const handleMyAccountClick = () => {
    if (!!customer) {
      clearCartStorage()
      handleLogout()
    } else {
      handleLogin()
    }
  }

  return (
    <footer>
      <ContactUsBanner banner={banner} />
      <div className="mx-auto max-w-container md:px-0 lg:px-12 desktop:max-w-content desktop:px-0">
        <div className="flex items-center justify-between border-b border-grey-200 px-4 py-6 md:px-12 md:py-8 lg:px-0 lg:py-12">
          {logoUrl && <Image src={logoUrl} alt="Footer Logo" width={142} height={40} />}
          <button className="border-b border-primary-500 text-sm font-medium text-primary-500" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            Back to top
          </button>
        </div>
        <div className="px-4 md:p-12 lg:px-0">
          <div className="flex flex-col py-8 md:flex-row md:py-0">
            <div className="mb-10 mr-0 flex flex-col gap-8 pr-0 md:mb-0 md:mr-12 md:border-r md:pr-12">
              <div className="max-w-44">
                <h2 className="mb-3 text-base font-semibold text-primary-900">My Account</h2>
                <button className="flex items-center gap-2 text-base font-medium not-italic leading-none text-grey-700" onClick={handleMyAccountClick}>
                  <UserCircleIcon size={18} className="text-primary-500" />
                  {accountButtonLabel}
                </button>
              </div>
              <Newsletter newsletterLabel={newsletterLabel} icons={socialIcons} />
            </div>
            <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4">
              {menus?.map(({ fields }, i) => {
                return <FooterLinks key={i} heading={fields?.title} links={fields?.links} />
              })}
            </div>
          </div>
        </div>
      </div>
      {socialIcons && socialIcons.length > 0 && (
        <ul className="flex items-center justify-center gap-x-6 border-t border-grey-200 py-8 md:hidden">
          <SocialIcons icons={socialIcons} />
        </ul>
      )}
      <CopyrightFooter />
    </footer>
  )
}

export default FooterClient
